import { AppBar, Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../asset/logo/LOGO_ORANGE.svg";
import { useTranslation } from "react-i18next";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import UnsubscribeOutlinedIcon from "@mui/icons-material/UnsubscribeOutlined";
import SportsEsportsOutlinedIcon from "@mui/icons-material/SportsEsportsOutlined";
import MilitaryTechOutlinedIcon from "@mui/icons-material/MilitaryTechOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import FeedbackDialog from "./FeedbackDialog";
import { isFeedbackEnabled } from "../service/FeedbackService";

export const DRAWER_WIDTH = 248;
/** Højden på topbjælken på telefon (menuen ligger bag knappen der) */
export const MOBILE_BAR_HEIGHT = 56;

// Sider kun en superadmin må bruge (administratorer, indhold, indstillinger, logs, cron-jobs)
const SUPERADMIN_ONLY = ["/manage-admin", "/manage-breef", "/manage-category", "/game", "/levels", "/mails", "/media", "/breef-setting", "/breef-tracking", "/logs", "/cron-jobs", "/queues", "/platform-settings", "/legal-documents"];

const currentUser = (): any => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};
const isSupport = () => currentUser().adminRole === "support";

// Menuen i grupper, så de 15 sider kan overskues. Rækkefølge: hverdag først, system til sidst.
const GROUPS: { key: string; items: { to: string; key: string; icon: JSX.Element }[] }[] = [
  {
    key: "nav.groupOverview",
    items: [
      { to: "/home", key: "nav.t1", icon: <DashboardOutlinedIcon /> },
      { to: "/stats", key: "nav.t15", icon: <InsightsOutlinedIcon /> },
    ],
  },
  {
    key: "nav.groupCustomers",
    items: [
      { to: "/manage-tenant", key: "nav.t3", icon: <BusinessOutlinedIcon /> },
      { to: "/manage-users", key: "nav.t9", icon: <GroupOutlinedIcon /> },
      { to: "/manage-teams", key: "nav.t11", icon: <GroupsOutlinedIcon /> },
      { to: "/survey", key: "nav.t5", icon: <UnsubscribeOutlinedIcon /> },
    ],
  },
  {
    key: "nav.groupContent",
    items: [
      { to: "/manage-breef", key: "nav.breefs", icon: <MovieOutlinedIcon /> },
      { to: "/game", key: "nav.t6", icon: <SportsEsportsOutlinedIcon /> },
      { to: "/levels", key: "nav.levels", icon: <MilitaryTechOutlinedIcon /> },
      { to: "/manage-category", key: "nav.t4", icon: <CategoryOutlinedIcon /> },
      { to: "/mails", key: "nav.mails", icon: <MarkEmailReadOutlinedIcon /> },
      { to: "/media", key: "nav.media", icon: <PermMediaOutlinedIcon /> },
      { to: "/breef-setting", key: "nav.t8", icon: <TuneOutlinedIcon /> },
    ],
  },
  {
    key: "nav.groupOperations",
    items: [
      { to: "/logs", key: "nav.t10", icon: <ArticleOutlinedIcon /> },
      { to: "/cron-jobs", key: "nav.t13", icon: <ScheduleOutlinedIcon /> },
      { to: "/queues", key: "nav.queues", icon: <SendOutlinedIcon /> },
    ],
  },
  {
    key: "nav.groupSystem",
    items: [
      { to: "/manage-admin", key: "nav.t2", icon: <AdminPanelSettingsOutlinedIcon /> },
      { to: "/platform-settings", key: "nav.t14", icon: <SettingsOutlinedIcon /> },
      { to: "/legal-documents", key: "nav.t16", icon: <GavelOutlinedIcon /> },
    ],
  },
];

export default function SideNavigation() {
  const [t] = useTranslation("global");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const user = currentUser();
  const support = isSupport();
  // "Giv feedback" (to RH Design) sits with the profile at the bottom, for every admin role, when it is set up
  const [feedbackEnabled, setFeedbackEnabled] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  useEffect(() => {
    isFeedbackEnabled().then(setFeedbackEnabled);
  }, []);

  // Menuen lukker, når man vælger en side (ellers ligger den hen over indholdet på telefonen)
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const itemSx = (active: boolean) => ({
    borderRadius: 2,
    mb: 0.25,
    py: 0.6,
    color: active ? "primary.main" : "text.primary",
    bgcolor: active ? "rgba(235,102,8,0.10)" : "transparent",
    "& .MuiListItemIcon-root": { color: active ? "primary.main" : "text.secondary", minWidth: 36 },
    "&:hover": { bgcolor: active ? "rgba(235,102,8,0.14)" : "action.hover" },
  });

  const currentTitle = GROUPS.flatMap((group) => group.items).find((item) => item.to === pathname)?.key;

  const content = (
    <>
      <Toolbar sx={{ py: 2, px: 2.5, minHeight: "auto !important" }}>
        <img src={Logo} alt="Breefer" style={{ width: 132 }} />
      </Toolbar>

      <Box sx={{ px: 1.5, pb: 2, overflowY: "auto", flexGrow: 1 }}>
        {GROUPS.map((group) => {
          const items = group.items.filter((item) => !(support && SUPERADMIN_ONLY.includes(item.to)));
          if (!items.length) return null;
          return (
            <Box key={group.key} sx={{ mb: 1 }}>
              <Typography variant="overline" sx={{ px: 1.5, color: "text.secondary", letterSpacing: 1, fontSize: 11, lineHeight: 2 }}>
                {t(group.key)}
              </Typography>
              <List disablePadding>
                {items.map((item) => (
                  <ListItemButton key={item.to} component={Link} to={item.to} sx={itemSx(pathname === item.to)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={t(item.key)} primaryTypographyProps={{ fontSize: 14.5 }} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>

      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Typography variant="body2" noWrap title={user.email}>
            {[user.firstName, user.lastName].filter(Boolean).join(" ") || user.email}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {support ? t("nav.roleSupport") : t("nav.roleSuperadmin")}
          </Typography>
        </Box>
        {feedbackEnabled && (
          <ListItemButton
            onClick={() => {
              setOpen(false);
              setFeedbackOpen(true);
            }}
            sx={itemSx(false)}
          >
            <ListItemIcon>
              <FeedbackOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.feedback")} primaryTypographyProps={{ fontSize: 14.5 }} />
          </ListItemButton>
        )}
        <ListItemButton component={Link} to="/logout" sx={itemSx(false)}>
          <ListItemIcon>
            <LogoutOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary={t("nav.t7")} primaryTypographyProps={{ fontSize: 14.5 }} />
        </ListItemButton>
      </Box>
    </>
  );

  const feedback = feedbackEnabled ? <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} /> : null;

  if (isMobile) {
    return (
      <>
        {feedback}
        <AppBar position="fixed" color="inherit" elevation={0} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
          <Toolbar sx={{ minHeight: `${MOBILE_BAR_HEIGHT}px !important`, gap: 1 }}>
            <IconButton edge="start" onClick={() => setOpen(true)} aria-label={t("nav.menu")}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h3" noWrap sx={{ fontSize: 17 }}>
              {currentTitle ? t(currentTitle) : "Breefer"}
            </Typography>
          </Toolbar>
        </AppBar>
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" } }}
        >
          {content}
        </Drawer>
      </>
    );
  }

  return (
    <>
      {feedback}
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box", borderRight: "1px solid", borderColor: "divider" },
        }}
      >
        {content}
      </Drawer>
    </>
  );
}
